import React, { useState, useEffect } from 'react';
import { 
  collection, query, orderBy, onSnapshot, doc, getDoc, 
  where, addDoc, serverTimestamp, arrayUnion, updateDoc 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { 
  Box, Typography, Avatar, List, ListItem, ListItemAvatar, ListItemText, 
  IconButton, InputBase, Modal, TextField, Button, Stack, CircularProgress, Alert, Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import BlockIcon from '@mui/icons-material/Block';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { db, auth, storage } from '../firebase';
import BottomNavigationBar from './BottomNavigationBar';

const MessagesScreen = () => {
  const [chats, setChats] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  // --- Modal State ---
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [selectedChatForReport, setSelectedChatForReport] = useState(null);
  const [reportRemarks, setReportRemarks] = useState('');
  const [reportFile, setReportFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [actionSuccess, setActionSuccess] = useState(null); // 'reported' or 'blocked'

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    // 1. Fetch current user's block list first
    const fetchBlocked = async () => {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        setBlockedUsers(userDoc.data().blockedUsers || []);
      }
    };
    fetchBlocked();

    // 2. Fetch Chats
    const chatsQuery = query(
      collection(db, 'chats'),
      where('participants', 'array-contains', currentUser.uid),
      orderBy('lastMessageTime', 'desc')
    );

    const unsubscribe = onSnapshot(chatsQuery, async (snapshot) => {
      const chatList = [];
      for (const docSnapshot of snapshot.docs) {
        const chatData = docSnapshot.data();
        const otherUserId = chatData.participants.find(id => id !== currentUser.uid);
        
        // Skip chats with blocked users
        if (blockedUsers.includes(otherUserId)) continue;

        const userDoc = await getDoc(doc(db, 'users', otherUserId));
        const userData = userDoc.exists() ? userDoc.data() : {};
        
        chatList.push({
          chatId: docSnapshot.id,
          otherUserId: otherUserId,
          userName: userData.displayName || userData.firstName || 'Unknown',
          profilePictureUrl: userData.photoURL || userData.profilePictureUrl || 'https://via.placeholder.com/40',
          lastMessage: chatData.lastMessage || 'You matched!',
          lastMessageTime: chatData.lastMessageTime?.toDate() || new Date(),
        });
      }
      setChats(chatList);
    });

    return () => unsubscribe();
  }, [blockedUsers.length]); // Re-run if block list changes

  const handleOpenReport = (e, chat) => {
    e.stopPropagation();
    setSelectedChatForReport(chat);
    setReportModalOpen(true);
  };

  const handleCloseReport = () => {
    setReportModalOpen(false);
    setSelectedChatForReport(null);
    setReportRemarks('');
    setReportFile(null);
    setActionSuccess(null);
  };

  // --- NEW: Block Logic ---
  const handleBlockUser = async () => {
    if (!window.confirm(`Are you sure you want to block ${selectedChatForReport.userName}? You will no longer see their messages.`)) return;

    setIsProcessing(true);
    try {
      const currentUser = auth.currentUser;
      const userRef = doc(db, 'users', currentUser.uid);

      // Add to blockedUsers array in Firestore
      await updateDoc(userRef, {
        blockedUsers: arrayUnion(selectedChatForReport.otherUserId)
      });

      // Update local state to trigger UI refresh
      setBlockedUsers(prev => [...prev, selectedChatForReport.otherUserId]);
      setActionSuccess('blocked');
      setTimeout(() => handleCloseReport(), 2000);
    } catch (error) {
      console.error("Block failed:", error);
      alert("Could not block user.");
    } finally {
      setIsProcessing(false);
    }
  };

  // --- Report Logic ---
  const submitReport = async () => {
    if (!reportRemarks.trim()) return alert("Please provide details for the report.");
    
    setIsProcessing(true);
    try {
      const currentUser = auth.currentUser;
      let proofUrl = '';

      if (reportFile) {
        const fileExt = reportFile.name.split('.').pop();
        const storageRef = ref(storage, `reports/${currentUser.uid}_${Date.now()}.${fileExt}`);
        const uploadResult = await uploadBytes(storageRef, reportFile);
        proofUrl = await getDownloadURL(uploadResult.ref);
      }

      await addDoc(collection(db, 'reports'), {
        reporterId: currentUser.uid,
        reportedUserId: selectedChatForReport.otherUserId,
        chatId: selectedChatForReport.chatId,
        remarks: reportRemarks,
        screenshotUrl: proofUrl,
        status: 'pending',
        timestamp: serverTimestamp(),
      });

      setActionSuccess('reported');
      setTimeout(() => handleCloseReport(), 2000);
    } catch (error) {
      console.error("Report failed:", error);
      alert("Could not submit report.");
    } finally {
      setIsProcessing(false);
    }
  };

  const filteredChats = chats.filter(chat =>
    chat.userName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Typography variant="h5" sx={styles.headerTitle}>Messages</Typography>
        <Box sx={styles.searchContainer}>
          <SearchIcon sx={styles.searchIcon} />
          <InputBase
            placeholder="Search chats..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={styles.searchInput}
          />
        </Box>
      </Box>

      <List sx={styles.chatList}>
        {filteredChats.map((chat) => (
          <ListItem
            key={chat.chatId}
            sx={styles.chatItem}
            onClick={() => navigate(`/chat/${chat.chatId}`)}
            secondaryAction={
<Button 
  size="small"
  onClick={(e) => handleOpenReport(e, chat)}
  sx={{ 
    color: '#bdc3c7', 
    textTransform: 'none', 
    fontWeight: 600,
    fontSize: '12px',
    '&:hover': {
      color: '#C00068',
      backgroundColor: 'transparent',
      textDecoration: 'underline'
    }
  }}
>
  Report/Block
</Button>
            }
          >
            <ListItemAvatar>
              <Avatar src={chat.profilePictureUrl} sx={styles.avatar} />
            </ListItemAvatar>
            <ListItemText
              primary={<Typography sx={styles.chatName}>{chat.userName}</Typography>}
              secondary={chat.lastMessage}
            />
          </ListItem>
        ))}
      </List>

      {/* --- REPORT & BLOCK MODAL --- */}
      <Modal open={reportModalOpen} onClose={handleCloseReport}>
        <Box sx={styles.modalStyle}>
          {actionSuccess ? (
            <Alert severity="success">
              {actionSuccess === 'blocked' ? "User has been blocked." : "Report submitted successfully."}
            </Alert>
          ) : (
            <>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight={700}>Safety Options</Typography>
                <IconButton onClick={handleCloseReport}><CloseIcon /></IconButton>
              </Stack>
              
              {/* Block Option */}
              <Button 
                variant="outlined" 
                color="error" 
                fullWidth 
                startIcon={<BlockIcon />}
                onClick={handleBlockUser}
                disabled={isProcessing}
                sx={{ mb: 3, borderRadius: '10px', textTransform: 'none', py: 1 }}
              >
                Block {selectedChatForReport?.userName}
              </Button>

              <Divider sx={{ mb: 3 }}><Typography variant="caption" color="text.secondary">OR REPORT</Typography></Divider>

              <TextField
                fullWidth multiline rows={3}
                placeholder="Describe the issue..."
                value={reportRemarks}
                onChange={(e) => setReportRemarks(e.target.value)}
                sx={{ mb: 2 }}
              />

              <Button
                component="label"
                variant="outlined"
                fullWidth
                startIcon={<CloudUploadIcon />}
                sx={{ mb: 3, textTransform: 'none', borderStyle: 'dashed' }}
              >
                {reportFile ? reportFile.name : "Add Screenshot (Optional)"}
                <input type="file" hidden accept="image/*" onChange={(e) => setReportFile(e.target.files[0])} />
              </Button>

              <Button 
                variant="contained" 
                fullWidth 
                onClick={submitReport}
                disabled={isProcessing}
                sx={{ bgcolor: '#C00068', borderRadius: '50px', py: 1.5, fontWeight: 700 }}
              >
                {isProcessing ? <CircularProgress size={24} color="inherit" /> : "Submit Report"}
              </Button>
            </>
          )}
        </Box>
      </Modal>

      <BottomNavigationBar />
    </Box>
  );
};

const styles = {
  container: { flex: 1, minHeight: '100vh', pb: '60px', display: 'flex', flexDirection: 'column' },
  header: { background: 'linear-gradient(to right, #B200BB, #C00068)', padding: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'sticky', top: 0, zIndex: 1000 },
  headerTitle: { color: '#FFFFFF', fontWeight: 'bold', fontSize: '24px', mb: '10px' },
  searchContainer: { display: 'flex', alignItems: 'center', backgroundColor: '#fff', borderRadius: '20px', padding: '6px 12px', width: '100%', maxWidth: '450px' },
  searchIcon: { color: '#C00068', marginRight: '10px' },
  searchInput: { flex: 1, fontSize: '16px' },
  chatList: { flex: 1, overflowY: 'auto' },
  chatItem: { padding: '12px 16px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer' },
  avatar: { width: '50px', height: '50px', border: '2px solid #C00068' },
  chatName: { fontSize: '17px', fontWeight: '600' },
  messageContainer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  lastMessage: { fontSize: '14px', color: '#666', maxWidth: '75%' },
  timestamp: { fontSize: '11px', color: '#888' },
  modalStyle: {
    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: 400 }, bgcolor: 'background.paper', borderRadius: '20px', p: 4, boxShadow: 24,
  }
};

export default MessagesScreen;